import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Check, Award, Users, Target, Heart, ArrowRight, Clock, TrendingUp, CheckCircle, Star, Phone, MapPin , MessageCircle } from "lucide-react";
import SEO from "../components/SEO";
import LandingPageHeader from "../components/LandingPageHeader";
import LandingPageFooter from "../components/LandingPageFooter";
import ServicePopup from "../components/ServicePopup";
import { portfolioProjects, testimonials } from "../mock";
import countryCodes from "../utils/countryCodes";
import { openChat } from "../utils/chatUtils";
import AnimatedIcon from "../components/AnimatedIcon";
import AOS from "aos";
import "aos/dist/aos.css";
import { useFormSubmit } from "../hooks/useFormSubmit";

const BookEditingLP = () => {
  const [activeGenre, setActiveGenre] = useState("Children\'s Book");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [activeTestimonialSlide, setActiveTestimonialSlide] = useState(0);

  const openServicePopup = () => {
    setIsPopupOpen(true);
  };

  const closeServicePopup = () => {
    setIsPopupOpen(false);
  };

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    countryCode: "+1",
    email: "",
    message: "",
    service: "Book Editing"
  });

  // Use centralized form submission hook
  const { isSubmitting, formStatus, handleSubmit, geoData, isLoadingGeo } = useFormSubmit();

  const genres = ["Children\'s Book", "Fiction", "Memoir", "Non-Fiction", "Poetry"];
  const filteredProjects = portfolioProjects.filter(project => project.genre === activeGenre);

  // Auto-slide testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonialSlide(prev => prev + 1);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Auto-open popup after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPopupOpen(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-out",
      once: true,
      offset: 100
    });
    window.scrollTo(0, 0);
  }, []);

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: "", message: "" });

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const response = await axios.post(`${backendUrl}/api/leads`, {
        name: formData.name,
        phone: formData.phone,
        country_code: formData.countryCode,
        email: formData.email,
        service: formData.service
      });

      if (response.status === 200) {
        setFormStatus({ 
          type: "success", 
          message: "Thank you! We will contact you shortly." 
        });
        setFormData({
          name: "",
          phone: "",
          countryCode: "+1",
          email: "",
    message: "",
          service: "Book Editing"
        });
      }
    } catch (error) {
      setFormStatus({ 
        type: "error", 
        message: "Something went wrong. Please try again." 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#000000]">
      <SEO 
        title="Book Editing Services - Professional Manuscript Editing"
        description="Expert editing services from developmental editing to proofreading. Make your manuscript shine with 70% discount!"
        keywords="book editing, manuscript editing, proofreading, copy editing, developmental editing"
        url="/lp/book-editing"
      />

      <LandingPageHeader />

      {/* Hero Section with Form */}
      <section className="hero-section-split relative min-h-screen flex items-center py-12 sm:py-16 md:py-20 mt-20 sm:mt-24 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="hero-background">
          <img
            src="https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=1920"
            alt="Book Editing Services"
            className="hero-image"
          />
          <div className="hero-overlay-split"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-7xl mx-auto">
            {/* Left Content */}
            <div data-aos="fade-right">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#F4D03F] mb-6 leading-tight">
                Polish Your Manuscript to Perfection
              </h1>
              <div className="flex items-center gap-3 sm:gap-5 text-[#dfddd6] text-sm mb-8">
                <div className="flex items-center gap-1.5">
                  <Check className="text-[#F4D03F] flex-shrink-0" size={16} />
                  <span className="whitespace-nowrap"><span className="text-[#F4D03F] font-semibold">Editing</span></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Check className="text-[#F4D03F] flex-shrink-0" size={16} />
                  <span className="whitespace-nowrap"><span className="text-[#F4D03F] font-semibold">Proofreading</span></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Check className="text-[#F4D03F] flex-shrink-0" size={16} />
                  <span className="whitespace-nowrap"><span className="text-[#F4D03F] font-semibold">Copy Editing</span></span>
                </div>
              </div>
              
              <div className="flex items-center gap-4 mt-6">
                <Button 
                  onClick={openChat}
                  className="bg-[#F4D03F] hover:bg-[#F8D26B] text-[#000000] font-bold px-6 py-3 rounded-lg transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl"
                >
                  <MessageCircle size={20} />
                  <span>Chat With Us Now</span>
                </Button>
              </div>
            </div>

            {/* Right Form */}
            <div className="hero-form-container" data-aos="fade-left">
              <Card className="bg-gradient-to-br from-[#2C3E50] to-[#1a1a1a] border-2 border-[#F4D03F] shadow-2xl backdrop-blur-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-16 sm:w-24 h-16 sm:h-24 bg-[#F4D03F] opacity-10 rounded-bl-full"></div>
                <div className="absolute bottom-0 left-0 w-12 sm:w-20 h-12 sm:h-20 bg-[#F8D26B] opacity-10 rounded-tr-full"></div>
                <CardHeader className="relative z-10 p-3 sm:p-5">
                  <CardTitle className="text-[#F4D03F] text-2xl sm:text-3xl font-bold uppercase text-center mb-2">
                    TRANSFORM GOOD WRITING INTO GREATNESS
                  </CardTitle>
                  <CardDescription className="text-[#ffffff] text-center" style={{ fontSize: "1.25rem" }}>
                    Professional editors refine every word, elevating your manuscript to publisher-quality standards.
                  </CardDescription>
                </CardHeader>
                <CardContent className="relative z-10 p-3 sm:p-5">
                  <form onSubmit={handleFormSubmit} className="space-y-2 sm:space-y-3">
                    <div><Input type="text" name="name" value={formData.name} onChange={handleFormChange} placeholder="Enter your full name" required aria-label="Your Name" className="bg-[#000000] border-2 border-[#2C3E50] text-[#ffffff] placeholder:text-[#888680] focus:border-[#F4D03F] h-10 sm:h-12 text-sm sm:text-base rounded-lg transition-all duration-300 hover:border-[#F8D26B]" /></div>
                    <div><Input type="email" name="email" value={formData.email} onChange={handleFormChange} placeholder="your.email@example.com" required aria-label="Your Email" className="bg-[#000000] border-2 border-[#2C3E50] text-[#ffffff] placeholder:text-[#888680] focus:border-[#F4D03F] h-10 sm:h-12 text-sm sm:text-base rounded-lg transition-all duration-300 hover:border-[#F8D26B]" /></div>
                    <div><div className="flex gap-2"><select name="countryCode" value={formData.countryCode} onChange={handleFormChange} aria-label="Country Code" className="bg-[#000000] border-2 border-[#2C3E50] text-[#ffffff] rounded-lg px-2 py-2 text-sm sm:text-base focus:outline-none focus:border-[#F4D03F] transition-all duration-300 hover:border-[#F8D26B]" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>{countryCodes.map((country) => (<option key={country.code} value={country.code}>{country.country} {country.code}</option>))}</select><Input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} placeholder="(123) 456-7890" required aria-label="Your Phone" className="flex-1 bg-[#000000] border-2 border-[#2C3E50] text-[#ffffff] placeholder:text-[#888680] focus:border-[#F4D03F] h-10 sm:h-12 text-sm sm:text-base rounded-lg transition-all duration-300 hover:border-[#F8D26B]" /></div></div>
                    <div><Textarea name="message" value={formData.message} onChange={handleFormChange} placeholder="Tell us about your editing needs..." rows={3} aria-label="Your Message" className="bg-[#000000] border-2 border-[#2C3E50] text-[#ffffff] placeholder:text-[#888680] focus:border-[#F4D03F] text-sm sm:text-base rounded-lg transition-all duration-300 hover:border-[#F8D26B]" /></div>
                    <Button type="submit" disabled={isSubmitting} className="btn-primary w-full h-11 sm:h-12 text-sm sm:text-base font-bold shadow-lg hover:shadow-2xl transition-all duration-300">{isSubmitting ? "Sending..." : "Claim Your Free Quote →"}</Button>
                  </form>
                  {formStatus.message && (
                    <div className={`mt-4 p-3 rounded-lg text-center ${formStatus.type === "success" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                      {formStatus.message}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>


      {/* Book Editing Services Section - Compact & Creative */}
      <section id="about" className="py-12 sm:py-16 bg-[#000000] relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: "repeating-linear-gradient(45deg, #D4AF37 0px, #D4AF37 1px, transparent 1px, transparent 12px)",
            backgroundSize: "17px 17px"
          }}></div>
        </div>
        
        {/* Bottom border accent for smooth transition */}
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent"></div>
        
        {/* Animated Background Icons */}
        <AnimatedIcon icon="award" position="top-right" size={90} delay={1} opacity={0.05} />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 items-stretch">
            {/* Left Content */}
            <div data-aos="fade-right" className="flex flex-col justify-between">
              {/* Premium badge */}
              <div className="inline-flex items-center gap-2 mb-4 px-3 sm:px-4 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 self-start">
                <span className="text-[#D4AF37] text-xs font-semibold uppercase tracking-wider">⚡ Polish Your Masterpiece</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#D4AF37] mb-3">EDITING THAT PERFECTS. MANUSCRIPTS THAT SHINE. TODAY.</h2>
              
              <p className="text-[#dfddd6] mb-4 leading-relaxed text-base sm:text-lg">
                <strong>Good writing becomes great with editing.</strong> Even the best authors need editors. Our <strong>professional book editing services</strong> eliminate errors, enhance clarity, and strengthen your narrative while preserving your unique voice-turning manuscripts into <strong>polished masterpieces</strong>.
              </p>
              
              <p className="text-[#888680] mb-6 leading-relaxed text-sm sm:text-base">
                <strong>Don't let mistakes ruin your book.</strong> We've helped 500+ authors perfect their manuscripts across 150+ countries. Genre-specialist editors with comprehensive feedback and tracked changes-<strong>starting now</strong>.
              </p>
              
              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 p-3 sm:p-4 bg-gradient-to-r from-[#D4AF37]/10 to-transparent rounded-lg border-l-2 border-[#D4AF37]">
                <div className="text-center">
                  <div className="text-[#D4AF37] font-black text-xl sm:text-2xl mb-1">500+</div>
                  <div className="text-[#888680] text-[10px] sm:text-xs uppercase">Edits</div>
                </div>
                <div className="text-center border-x border-[#D4AF37]/20">
                  <div className="text-[#D4AF37] font-black text-xl sm:text-2xl mb-1">150+</div>
                  <div className="text-[#888680] text-[10px] sm:text-xs uppercase">Countries</div>
                </div>
                <div className="text-center">
                  <div className="text-[#D4AF37] font-black text-xl sm:text-2xl mb-1">24/7</div>
                  <div className="text-[#888680] text-[10px] sm:text-xs uppercase">Support</div>
                </div>
              </div>
              
              {/* Compact feature list */}
              <div className="space-y-3 mb-6 flex-1">
                <div className="flex items-center gap-3 text-[#dfddd6] text-sm sm:text-base">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Check className="text-[#D4AF37]" size={16} strokeWidth={3} />
                  </div>
                  <div>
                    <span className="font-semibold text-[#D4AF37]">Comprehensive Editing</span> - Developmental, line, copy, and proofreading
                  </div>
                </div>
                
                <div className="flex items-center gap-3 text-[#dfddd6] text-sm sm:text-base">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Check className="text-[#D4AF37]" size={16} strokeWidth={3} />
                  </div>
                  <div>
                    <span className="font-semibold text-[#D4AF37]">Genre Specialists</span> - Editors matched to your specific genre
                  </div>
                </div>
                
                <div className="flex items-center gap-3 text-[#dfddd6] text-sm sm:text-base">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Check className="text-[#D4AF37]" size={16} strokeWidth={3} />
                  </div>
                  <div>
                    <span className="font-semibold text-[#D4AF37]">Detailed Feedback</span> - Track changes and actionable suggestions
                  </div>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button 
                  onClick={() => openServicePopup("Edit Your Book", "Polish your manuscript to perfection with comprehensive editing services.")}
                  className="h-11 px-4 sm:px-6 rounded-xl bg-[#D4AF37] text-[#0B0B0B] font-semibold text-sm tracking-[0.2px] hover:bg-[#E0BF5A] hover:shadow-gold transition-all duration-200 flex items-center justify-center gap-2 group"
                >
                  Edit Your Book Now
                  <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                </button>
                <button 
                  onClick={openChat}
                  className="h-11 px-4 sm:px-6 rounded-xl border-2 border-[#D4AF37] text-[#D4AF37] font-semibold text-sm tracking-[0.2px] hover:bg-[rgba(212,175,55,0.1)] transition-all duration-200 flex items-center justify-center gap-2 group"
                >
                  Discuss Your Project
                </button>
              </div>
            </div>
            
            {/* Right Content - Image & Additional Info */}
            <div data-aos="fade-left" className="flex flex-col gap-3 mt-6 lg:mt-0">
              <div className="relative h-[400px] sm:h-[450px] md:h-[500px]">
                {/* Decorative frame */}
                <div className="absolute -inset-3 bg-gradient-to-br from-[#D4AF37]/20 to-transparent rounded-2xl blur-xl"></div>
                <img
                  src="https://images.pexels.com/photos/7968063/pexels-photo-7968063.jpeg?w=800"
                  alt="Professional Book Editing Services"
                  className="rounded-xl shadow-2xl relative z-10 border-2 border-[#D4AF37]/30 w-full h-full object-cover"
                />
                {/* Stats overlay badge */}
                <div className="absolute bottom-4 right-4 bg-[#000000]/90 backdrop-blur-sm border border-[#D4AF37]/50 rounded-lg p-3 z-20">
                  <div className="text-[#D4AF37] font-black text-2xl mb-0.5">500+</div>
                  <div className="text-white text-xs">Books Perfected</div>
                </div>
              </div>
              
              {/* Additional Quick Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-3 rounded-lg border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all duration-300">
                  <div className="text-[#D4AF37] font-bold text-base mb-0.5">⚡ Error-Free Excellence</div>
                  <p className="text-[#888680] text-xs leading-tight">Professional polish with zero mistakes</p>
                </div>
                <div className="bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-3 rounded-lg border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all duration-300">
                  <div className="text-[#D4AF37] font-bold text-base mb-0.5">🚀 Fast Turnaround</div>
                  <p className="text-[#888680] text-xs leading-tight">Comprehensive edits in days, not weeks</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 sm:py-20 bg-[#302f2c]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#F4D03F] mb-4">Comprehensive Book Editing Process</h2>
            <p className="text-base sm:text-lg text-[#dfddd6] max-w-3xl mx-auto">
              Every word matters, and editing is where the magic happens. Our skilled editors refine your manuscript's clarity, flow, and consistency.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-[#000000] p-6 sm:p-8 rounded-xl border border-[#F4D03F]/30 hover:border-[#F4D03F] transition-all" data-aos="fade-up" data-aos-delay="0">
              <div className="text-[#F4D03F] text-5xl font-bold mb-4">01</div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#F4D03F] mb-3">Developmental Editing</h3>
              <p className="text-[#dfddd6]">We strengthen your narrative structure, character development, and overall story arc.</p>
            </div>

            <div className="bg-[#000000] p-6 sm:p-8 rounded-xl border border-[#F4D03F]/30 hover:border-[#F4D03F] transition-all" data-aos="fade-up" data-aos-delay="100">
              <div className="text-[#F4D03F] text-5xl font-bold mb-4">02</div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#F4D03F] mb-3">Copy Editing & Line Editing</h3>
              <p className="text-[#dfddd6]">Refine sentence structure, improve flow, and enhance clarity while maintaining your unique voice.</p>
            </div>

            <div className="bg-[#000000] p-6 sm:p-8 rounded-xl border border-[#F4D03F]/30 hover:border-[#F4D03F] transition-all" data-aos="fade-up" data-aos-delay="200">
              <div className="text-[#F4D03F] text-5xl font-bold mb-4">03</div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#F4D03F] mb-3">Proofreading & Final Polish</h3>
              <p className="text-[#dfddd6]">Eliminate typos, grammar errors, and formatting issues for a polished, professional manuscript.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section - Reused from Home */}
      <section id="portfolio" className="py-16 sm:py-20 md:py-24 bg-[#000000]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#F4D03F] mb-4 uppercase">OUR PORTFOLIO</h2>
            <p className="text-base sm:text-lg text-[#ffffff] max-w-2xl mx-auto">
              We don't just talk the talk, we write it. Take a look at some of our latest collaborations with authors across genres.
            </p>
          </div>

          {/* Genre Tabs */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12" data-aos="fade-up" data-aos-delay="100">
            {genres.map((genre) => (
              <button
                key={genre}
                onClick={() => setActiveGenre(genre)}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-xs sm:text-sm uppercase tracking-wide transition-all duration-300 ${
                  activeGenre === genre
                    ? "bg-[#F4D03F] text-[#000000] shadow-lg scale-105"
                    : "bg-[#2C3E50] text-[#ffffff] hover:bg-[#F8D26B] hover:text-[#000000]"
                }`}
              >
                {genre}
              </button>
            ))}
          </div>

          {/* Books Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {filteredProjects.map((project, index) => (
              <div
                key={project.id}
                className="portfolio-book-card group"
                data-aos="zoom-in"
                data-aos-delay={index * 50}
              >
                <Card className="bg-[#2C3E50] border-[#F8D26B] border-opacity-30 overflow-hidden h-full hover:border-[#F4D03F] transition-all duration-500 hover:shadow-2xl hover:shadow-[#F4D03F]/20">
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-2 sm:top-4 left-2 sm:left-4 bg-[#F8D26B] text-[#000000] px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold uppercase">
                      {project.genre}
                    </div>
                  </div>
                  <CardContent className="p-3 sm:p-6">
                    <h3 className="text-[#ffffff] font-bold text-sm sm:text-lg mb-2 group-hover:text-[#F4D03F] transition-colors truncate">
                      {project.title}
                    </h3>
                    <div className="flex justify-between items-center text-xs sm:text-sm text-[#F4D03F]">
                      <span className="flex items-center gap-1 truncate">
                        <Check size={12} className="text-[#F8D26B] flex-shrink-0" />
                        <span className="truncate">{project.author}</span>
                      </span>
                      <span className="flex-shrink-0 bg-[#F4D03F]/20 text-[#F4D03F] px-2 py-0.5 rounded text-xs font-semibold">
                        {project.status || "Published"}
                      </span>
                    </div>
                  </CardContent>
              </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - Unique Design */}
      <section className="py-16 sm:py-20 bg-[#302f2c] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-5">
          <img src="https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=800" alt="Expert Book Editing Services" className="w-full h-full object-cover" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#F4D03F] mb-4 uppercase">Why Work With Us?</h2>
            <p className="text-base sm:text-lg text-[#dfddd6] max-w-3xl mx-auto">
              We combine creative excellence with proven processes to deliver manuscripts that captivate readers and stand the test of time.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            <div className="bg-[#000000] p-6 sm:p-8 rounded-xl border-l-4 border-[#F4D03F] hover:transform hover:scale-105 transition-all duration-300" data-aos="fade-up" data-aos-delay="0">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F4D03F]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Award className="text-[#F4D03F]" size={24} />
                </div>
                <div>
                  <h3 className="text-[#F4D03F] font-bold text-lg mb-2">Expert Writers</h3>
                  <p className="text-[#dfddd6] text-sm">Seasoned professionals across all genres with proven track records.</p>
                </div>
              </div>
            </div>

            <div className="bg-[#000000] p-6 sm:p-8 rounded-xl border-l-4 border-[#F4D03F] hover:transform hover:scale-105 transition-all duration-300" data-aos="fade-up" data-aos-delay="100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F4D03F]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="text-[#F4D03F]" size={24} />
                </div>
                <div>
                  <h3 className="text-[#F4D03F] font-bold text-lg mb-2">On-Time Delivery</h3>
                  <p className="text-[#dfddd6] text-sm">We respect deadlines and deliver quality work on schedule.</p>
                </div>
              </div>
            </div>

            <div className="bg-[#000000] p-6 sm:p-8 rounded-xl border-l-4 border-[#F4D03F] hover:transform hover:scale-105 transition-all duration-300" data-aos="fade-up" data-aos-delay="200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F4D03F]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="text-[#F4D03F]" size={24} />
                </div>
                <div>
                  <h3 className="text-[#F4D03F] font-bold text-lg mb-2">100% Satisfaction</h3>
                  <p className="text-[#dfddd6] text-sm">Unlimited revisions until you're completely happy with your manuscript.</p>
                </div>
              </div>
            </div>

            <div className="bg-[#000000] p-6 sm:p-8 rounded-xl border-l-4 border-[#F4D03F] hover:transform hover:scale-105 transition-all duration-300" data-aos="fade-up" data-aos-delay="300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F4D03F]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="text-[#F4D03F]" size={24} />
                </div>
                <div>
                  <h3 className="text-[#F4D03F] font-bold text-lg mb-2">Collaborative Process</h3>
                  <p className="text-[#dfddd6] text-sm">Regular updates and your input at every stage of development.</p>
                </div>
              </div>
            </div>

            <div className="bg-[#000000] p-6 sm:p-8 rounded-xl border-l-4 border-[#F4D03F] hover:transform hover:scale-105 transition-all duration-300" data-aos="fade-up" data-aos-delay="400">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F4D03F]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Target className="text-[#F4D03F]" size={24} />
                </div>
                <div>
                  <h3 className="text-[#F4D03F] font-bold text-lg mb-2">Your Voice, Our Craft</h3>
                  <p className="text-[#dfddd6] text-sm">We capture your unique voice and vision in every word.</p>
                </div>
              </div>
            </div>

            <div className="bg-[#000000] p-6 sm:p-8 rounded-xl border-l-4 border-[#F4D03F] hover:transform hover:scale-105 transition-all duration-300" data-aos="fade-up" data-aos-delay="500">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F4D03F]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Heart className="text-[#F4D03F]" size={24} />
                </div>
                <div>
                  <h3 className="text-[#F4D03F] font-bold text-lg mb-2">Confidential & Secure</h3>
                  <p className="text-[#dfddd6] text-sm">Your ideas and manuscripts are protected with strict NDAs.</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12" data-aos="fade-up" data-aos-delay="600">
            <Button 
              onClick={openServicePopup}
              className="inline-flex h-14 px-10 rounded-xl bg-[#F4D03F] text-[#0B0B0B] font-semibold text-base hover:bg-[#F8D26B] transition-all duration-300 items-center justify-center gap-2 shadow-lg hover:shadow-xl"
            >
              Get Started Today
              <ArrowRight size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Mission/Story Section */}
      <section className="py-16 sm:py-20 bg-[#000000]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div data-aos="fade-right">
                <h2 className="text-3xl sm:text-4xl font-bold text-[#F4D03F] mb-4">Editing That Elevates Your Story</h2>
                <p className="text-[#dfddd6] text-base sm:text-lg mb-4 leading-relaxed">
                  At White House Publishers, we believe every manuscript has the potential to be extraordinary. Our editors work with precision and care to bring out the best in your writing.
                </p>
                <p className="text-[#888680] text-sm sm:text-base mb-6 leading-relaxed">
                  From developmental editing to meticulous proofreading, we ensure your manuscript reflects the very best version of your story.
                </p>
              </div>
              
              <div data-aos="fade-left">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1512820790803-83ca734da794?w=800"
                    alt="Turn Your Story Into a Masterpiece"
                    className="w-full h-64 sm:h-80 object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-r from-[#F4D03F] via-[#F8D26B] to-[#F4D03F]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div data-aos="fade-right">
                <h2 className="text-3xl sm:text-4xl font-bold text-[#0B0B0B] mb-2 leading-tight">
                  Your Bestseller Journey Starts Here!
                </h2>
                <p className="text-[#2C2C2C] text-base sm:text-lg leading-relaxed">
                  Transform your manuscript into a published masterpiece. Join 500+ successful authors who turned their dreams into reality-your story deserves to be told!
                </p>
              </div>
              
              <div className="flex flex-col gap-4 shrink-0" data-aos="fade-left">
                <Button 
                  onClick={openServicePopup}
                  className="inline-flex h-12 px-8 rounded-xl bg-[#0B0B0B] text-white font-semibold text-base hover:bg-[#2C2C2C] transition-all duration-300 items-center justify-center gap-2 whitespace-nowrap"
                >
                  Publish Your Book
                  <ArrowRight size={18} />
                </Button>
                <Button 
                  onClick={openChat}
                  className="js-chat inline-flex h-12 px-8 rounded-xl bg-transparent border-2 border-[#0B0B0B] text-[#0B0B0B] font-semibold text-base hover:bg-[#0B0B0B] hover:text-white transition-all duration-300 items-center justify-center gap-2 whitespace-nowrap"
                >
                  Discuss Your Project
                  <MessageCircle size={18} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Reused from Home */}
      <section className="py-16 sm:py-20 bg-[#000000]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#F4D03F] mb-4 uppercase">Client Success</h2>
            <p className="text-base sm:text-lg text-[#888680] max-w-3xl mx-auto mb-6">
              Let's be honest, you can market the hell out of a mediocre book, but if it's not well-written, it's not going to sell.
            </p>
          </div>

          <div className="relative max-w-7xl mx-auto py-8">
            <div className="overflow-hidden px-4">
              <div 
                className="flex items-center transition-transform duration-700 ease-in-out"
                style={{ 
                  transform: `translateX(calc(-${(activeTestimonialSlide % testimonials.length) * 33.333}% + ${100 / 3}%))`,
                }}
              >
                {[...testimonials, ...testimonials].map((testimonial, index) => {
                  const normalizedIndex = index % testimonials.length;
                  const isActive = normalizedIndex === (activeTestimonialSlide % testimonials.length);
                  return (
                    <div 
                      key={`${testimonial.id}-${index}`} 
                      className="w-1/3 px-4 flex-shrink-0 transition-all duration-700"
                      style={{
                        transform: isActive && index < testimonials.length ? "scale(1.1)" : "scale(0.85)",
                        filter: isActive && index < testimonials.length ? "blur(0px)" : "blur(3px)",
                        opacity: isActive && index < testimonials.length ? 1 : 0.5,
                        zIndex: isActive && index < testimonials.length ? 10 : 1
                      }}
                    >
                      <Card className="bg-[#302f2c] border-[#3f4816] hover:border-[#F4D03F] transition-all duration-300 h-full">
                        <CardContent className="p-6 sm:p-8">
                          <div className="flex gap-1 mb-4 justify-center">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <Star key={i} size={18} className="fill-[#F4D03F] text-[#F4D03F]" />
                            ))}
                          </div>
                          <p className="text-[#dfddd6] mb-6 italic text-base leading-relaxed text-center min-h-[120px]">"{testimonial.content}"</p>
                          <div className="border-t border-[#3f4816] pt-4 text-center">
                            <div className="text-[#F4D03F] font-bold text-base">{testimonial.author}</div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Slider Indicators */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonialSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === (activeTestimonialSlide % testimonials.length) 
                      ? "bg-[#F4D03F] w-8" 
                      : "bg-[#888680]"
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <LandingPageFooter />

      {/* Service Popup */}
      <ServicePopup 
        isOpen={isPopupOpen}
        onClose={closeServicePopup}
      />
    
    </div>
  );
};

export default BookEditingLP;
